import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, Loader2, ArrowRight } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_643f3f34-a1d4-4c71-8acb-3c1d3f8beb8d/artifacts/bl51k3pb_95533D89-11D2-45C0-9D79-4F0FFEBEC907.png";

export default function AdminLogin() {
  const { login, user, checked } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (checked && user) {
      navigate(from, { replace: true });
    }
  }, [checked, user, from, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "Login failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-160px)] flex items-center justify-center px-6 py-16">
      <div
        data-testid="admin-login-card"
        className="w-full max-w-md border border-[#27272a] bg-[#0e0e0e] rounded-sm p-8 md:p-10 relative"
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-[#C8A24B]" />
        <Link to="/" className="flex items-center gap-3 mb-8">
          <img src={LOGO_URL} alt="Agent Marketing" className="h-10 w-10 object-contain" />
          <div>
            <div className="font-heading tracking-widest text-sm text-white uppercase">
              Agent<span className="text-[#C8A24B] ml-1">Marketing</span>
            </div>
            <div className="text-[9px] tracking-[0.28em] text-[#71717a] uppercase mt-0.5">
              Admin Console
            </div>
          </div>
        </Link>

        <div className="mb-8">
          <div className="text-[10px] tracking-[0.28em] uppercase text-[#C8A24B] mb-3 flex items-center gap-2">
            <Lock size={12} />
            Restricted Area
          </div>
          <h1 className="font-heading text-3xl text-white leading-tight">
            Sign in to your admin console.
          </h1>
        </div>

        <form onSubmit={submit} data-testid="admin-login-form" className="space-y-5">
          <label className="block">
            <span className="block text-[10px] tracking-[0.24em] uppercase text-[#71717a] mb-2 font-medium">
              Email
            </span>
            <input
              data-testid="admin-login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-sm px-4 py-3 text-white placeholder:text-[#52525b] focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B] outline-none"
              placeholder="you@agentmark.ca"
              autoComplete="email"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] tracking-[0.24em] uppercase text-[#71717a] mb-2 font-medium">
              Password
            </span>
            <input
              data-testid="admin-login-password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#0a0a0a] border border-[#27272a] rounded-sm px-4 py-3 text-white placeholder:text-[#52525b] focus:border-[#C8A24B] focus:ring-1 focus:ring-[#C8A24B] outline-none"
              placeholder="Enter your password"
              autoComplete="current-password"
            />
          </label>

          {error && (
            <div
              data-testid="admin-login-error"
              className="text-sm text-[#f87171] border border-[#7f1d1d] bg-[#7f1d1d]/10 p-3 rounded-sm"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="admin-login-submit"
            className="btn-gold w-full disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <Loader2 size={16} className="animate-spin" /> Signing in
              </>
            ) : (
              <>
                Sign in <ArrowRight size={16} />
              </>
            )}
          </button>
        </form>

        <div className="mt-8 pt-6 border-t border-[#27272a] text-xs text-[#71717a] leading-relaxed">
          This area is restricted to Agent Marketing administrators. If you
          need access, contact <a href="mailto:contact@kostaja.ca" className="text-[#C8A24B]">contact@kostaja.ca</a>.
        </div>
      </div>
    </div>
  );
}
